import type { CalculatorInput, CalculationResult } from "@schulte/calculator";
import { LEAD_SCHEMA_VERSION } from "@schulte/lead-schema/constants";
import type { LeadAcceptedResponse, LeadContact, LeadSubmission } from "@schulte/lead-schema";
import type { TouchpointRecord } from "@schulte/touchpoint-tracker";

export interface BuildSubmissionParams {
  idempotencyKey: string;
  contact: LeadContact;
  input: CalculatorInput;
  result: CalculationResult;
  pageUrl: string;
  locale: string;
  privacyNoticeVersion: string;
  clientVersion: string;
  fillTimeMs: number;
  honeypot: string;
  touchpoints: TouchpointRecord | null;
}

/** Assembles the request body. Pure, so it can be tested against the schema. */
export function buildLeadSubmission(p: BuildSubmissionParams): LeadSubmission {
  return {
    schemaVersion: LEAD_SCHEMA_VERSION,
    idempotencyKey: p.idempotencyKey,
    contact: {
      name: p.contact.name.trim(),
      company: p.contact.company.trim(),
      email: p.contact.email.trim(),
      ...(p.contact.phone?.trim() ? { phone: p.contact.phone.trim() } : {}),
    },
    calculator: { input: p.input, result: p.result },
    context: {
      pageUrl: p.pageUrl,
      locale: p.locale,
      privacyNoticeVersion: p.privacyNoticeVersion,
      clientVersion: p.clientVersion,
      fillTimeMs: Math.max(0, Math.round(p.fillTimeMs)),
    },
    antiSpam: { website: p.honeypot },
    touchpoints: p.touchpoints,
  };
}

export type SubmitOutcome =
  | { ok: true; id: string }
  | { ok: false; kind: "validation" | "rate_limited" | "server" | "network" };

export async function submitLead(
  apiUrl: string,
  body: LeadSubmission,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmitOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(apiUrl, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, kind: "network" };
  }

  if (response.status === 201 || response.status === 200) {
    try {
      const data = (await response.json()) as Partial<LeadAcceptedResponse>;
      if (typeof data.id === "string") return { ok: true, id: data.id };
    } catch {
      // fall through
    }
    return { ok: false, kind: "server" };
  }
  if (response.status === 400 || response.status === 413 || response.status === 422) return { ok: false, kind: "validation" };
  if (response.status === 429) return { ok: false, kind: "rate_limited" };
  return { ok: false, kind: "server" };
}
